import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  input,
  Input,
  output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { OfferSelectComponent } from '@core/offers-smart-ui';
import type { OfferSelectOption, PlacementInfo } from '@core/offers-ui';
import { OfferInfoBoxComponent } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import {
  BadgeComponent,
  HeadingComponent,
  ProtectedControlDirective,
  RtStatefulComponent,
  TextareaComponent,
} from '@core/user-interface-legacy';
import type { RedactorConfig } from '@core/wysiwyg-legacy';
import { RedactorComponent, RedactorService } from '@core/wysiwyg-legacy';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import { InterviewButtonSelectComponent } from '../interview-button-select/interview-button-select.component';

type State = {
  noteOpen: boolean;
};

@Component({
  selector: 'rt-interview-summary',
  templateUrl: 'interview-summary.component.html',
  styleUrls: ['./interview-summary.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    InterviewBaseBoxComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    HeadingComponent,
    OfferInfoBoxComponent,
    OfferSelectComponent,
    BadgeComponent,
    RedactorComponent,
    ProtectedControlDirective,
    TextareaComponent,
    InterviewButtonSelectComponent,
    TranslatePipe,
  ],
})
export class InterviewSummaryComponent extends RtStatefulComponent<State> {
  private readonly redactorSvc = inject(RedactorService);
  private readonly i18n = inject(I18nService);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly redactorConfig: RedactorConfig = {
    ...this.redactorSvc.defaultConfig,
    autoparseVideo: false,
    minHeight: '100px',
    maxHeight: '600px',
  };
  public readonly question: { questionTitle: string; questionRequired: boolean; summary: boolean } =
    {
      questionTitle: this.i18n.translate('app.interview.summary.title'),
      questionRequired: false,
      summary: true,
    };
  public readonly ratingMap: { [key: string]: string } = {
    strong_no: this.i18n.translate('app.interview.summary.strong_no'),
    no: this.i18n.translate('app.interview.summary.no'),
    neutral: this.i18n.translate('app.interview.summary.neutral'),
    yes: this.i18n.translate('app.interview.summary.yes'),
    strong_yes: this.i18n.translate('app.interview.summary.strong_yes'),
  };

  public readonly container = input<boolean>(true);
  public readonly short = input<boolean>(false);
  public readonly showSelectedValue = input<boolean>(false);
  public readonly useRedactor = input<boolean>(false);
  public readonly showRequiredLabel = input<boolean>(false);
  public readonly redactorAutofocus = input<boolean>(true);
  public readonly placements = input<Placement[]>();
  @Input() public parentForm: UntypedFormGroup;
  @Input() public offers: OfferSelectOption[];
  @Input() public assignedPlacement?: PlacementInfo | null;
  @Input()
  public set hasOpenNote(noteOpen: boolean) {
    this.setState({
      noteOpen,
    });
  }
  public hasProtectedControls = input<boolean, boolean | string>(true, {
    transform: booleanAttribute,
  });

  public rateChange = output<void>();

  private ngOnDestroy$: Subject<void> = new Subject();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      noteOpen: false,
    });
  }

  public openNote(): void {
    this.setState({
      noteOpen: true,
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngOnInit() {
    this.parentForm
      .get('offerId')
      ?.valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(offerId => {
        const stageId = this.placements()?.find(
          placement => offerId === placement.offerId,
        )?.stageId;

        if (stageId) {
          this.parentForm.get('stageId')!.setValue(stageId);
        } else {
          this.parentForm.patchValue({ stageId: null });
        }
      });
  }
}
